import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import api from "../../api/api";

const predefinedConditions = [
  { label: "Активный", value: { status: "active" } },
  { label: "Неактивный", value: { status: "inactive" } },
  { label: "Менеджер", value: { role: "manager" } },
  { label: "Администратор", value: { role: "admin" } },
];

const ActionConfigList = ({ businessTypeId }) => {
  const [actionConfigs, setActionConfigs] = useState([]);
  const [newAction, setNewAction] = useState({
    label: "",
    module: "",
    action: "",
    condition: {}, // Добавляем condition как объект
  });

  const [errorMessage, setErrorMessage] = useState("");
  const requestVersion = useRef(0);
  const currentBusiness = useRef(businessTypeId);
  currentBusiness.current = businessTypeId;

  // Загрузка экшенов
  const loadActions = async () => {
    if (currentBusiness.current !== businessTypeId) return;
    const version = ++requestVersion.current;
    setErrorMessage("");
    try {
      const response = await api.get(`/actions/actions-by-id`, {
        params: { businessTypeId },
      });
      if (version !== requestVersion.current) return;
      setActionConfigs(Array.isArray(response.data) ? response.data : []);
      console.log("Загруженные экшены:", response.data);
    } catch (error) {
      if (version === requestVersion.current) setErrorMessage("Не удалось загрузить действия");
      console.error("Ошибка при загрузке экшенов:", error);
    }
  };

  useEffect(() => {
    setActionConfigs([]);
    if (businessTypeId) {
      loadActions();
    }
    return () => { requestVersion.current += 1; };
  }, [businessTypeId]);

  // Добавление нового экшена
  const handleAddAction = async () => {
    if (
      !newAction.label.trim() ||
      !newAction.module.trim() ||
      !newAction.action.trim() ||
      !businessTypeId
    ) {
      alert("Все поля обязательны для заполнения!");
      return;
    }

    const newActionWithDefaults = {
      ...newAction,
      label: newAction.label.trim(),
      module: newAction.module.trim(),
      action: newAction.action.trim(),
      color: "blue",
      is_bulk: newAction.is_bulk || false,
      business_type_id: businessTypeId, // Передаём ID бизнеса
    };

    try {
      const response = await api.post(`/actions/`, newActionWithDefaults);
      console.log("Новый экшен добавлен:", response.data);

      // Перезагружаем список экшенов
      await loadActions();
      setNewAction({
        label: "",
        module: "",
        action: "",
        condition: {},
        is_bulk: false,
      });
    } catch (error) {
      setErrorMessage("Не удалось добавить действие");
      console.error("Ошибка при добавлении экшена:", error);
    }
  };

  // Редактирование экшена
  // Редактирование экшена
  const handleEditAction = async (actionId) => {
    const updatedAction = actionConfigs.find(
      (action) => action.id === actionId
    );

    if (!updatedAction) {
      alert("Экшен для редактирования не найден!");
      return;
    }

    // Открываем модальное окно или форму для редактирования
    const newLabel = prompt(
      "Введите новое название экшена:",
      updatedAction.label
    );
    const newModule = prompt(
      "Введите новый модуль экшена:",
      updatedAction.module
    );
    const newActionName = prompt(
      "Введите новое действие экшена:",
      updatedAction.action
    );
    const newCondition = prompt(
      "Введите новое условие (JSON):",
      JSON.stringify(updatedAction.condition)
    );

    if ([newLabel, newModule, newActionName, newCondition].some(value => value === null)) return;

    if (!newLabel.trim() || !newModule.trim() || !newActionName.trim()) {
      alert("Все поля обязательны для заполнения!");
      return;
    }

    let parsedCondition = updatedAction.condition;
    try {
      parsedCondition = newCondition ? JSON.parse(newCondition) : null;
      if (parsedCondition !== null && (Array.isArray(parsedCondition) || typeof parsedCondition !== "object")) throw new Error("Condition must be an object");
    } catch (err) {
      alert("Условие должно быть в формате JSON!");
      return;
    }

    const updatedActionData = {
      ...updatedAction,
      label: newLabel.trim(),
      module: newModule.trim(),
      action: newActionName.trim(),
      condition: parsedCondition,
    };

    try {
      const response = await api.put(`/actions/${actionId}`, updatedActionData);
      console.log("Экшен обновлен:", response.data);

      // Перезагружаем список экшенов
      await loadActions();
    } catch (error) {
      setErrorMessage("Не удалось изменить действие");
      console.error("Ошибка редактирования экшена:", error);
    }
  };

  // Удаление экшена
  const handleDeleteAction = async (actionId) => {
    if (window.confirm("Вы уверены, что хотите удалить этот экшен?")) {
      try {
        await api.delete(`/actions/${actionId}`);
        console.log(`Экшен с ID ${actionId} удален`);

        // Перезагружаем список экшенов
        await loadActions();
      } catch (error) {
        setErrorMessage("Не удалось удалить действие");
      console.error("Ошибка удаления экшена:", error);
      }
    }
  };

  return (
    <div>
      <h3>Конфигурация экшенов</h3>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Название</TableCell>
            <TableCell>Модуль</TableCell>
            <TableCell>Действие</TableCell>
            <TableCell>Условие</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(actionConfigs || []).map((action) => (
            <TableRow key={action.id}>
              <TableCell>{action.label}</TableCell>
              <TableCell>{action.module}</TableCell>
              <TableCell>{action.action || "Нет"}</TableCell>
              <TableCell>
                {JSON.stringify(action.condition) || "Нет условия"}
              </TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleEditAction(action.id)}
                >
                  Редактировать
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleDeleteAction(action.id)}
                >
                  Удалить
                </Button>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>
              <TextField
                label="Название"
                value={newAction.label}
                onChange={(e) =>
                  setNewAction({ ...newAction, label: e.target.value })
                }
                fullWidth
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Модуль"
                value={newAction.module}
                onChange={(e) =>
                  setNewAction({ ...newAction, module: e.target.value })
                }
                fullWidth
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Действие"
                value={newAction.action}
                onChange={(e) =>
                  setNewAction({ ...newAction, action: e.target.value })
                }
                fullWidth
              />
            </TableCell>
            <TableCell>
              <FormControl fullWidth>
                <InputLabel id="action-condition-label">Условие</InputLabel>
                <Select
                  labelId="action-condition-label"
                  value={JSON.stringify(newAction.condition) || ""}
                  onChange={(e) => {
                    const conditionValue = e.target.value
                      ? JSON.parse(e.target.value)
                      : null;
                    setNewAction({ ...newAction, condition: conditionValue });
                  }}
                >
                  <MenuItem value="{}">Без условия</MenuItem>
                  {predefinedConditions.map((cond, index) => (
                    <MenuItem key={index} value={JSON.stringify(cond.value)}>
                      {cond.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddAction}
              >
                Добавить
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default ActionConfigList;
